<template>
  <div
    class="drawer"
    :class="{ 'side-drawer--no-title-gap': !title, 'drawer--floating': floating, ['drawer--x-' + xPosition]: true, [`drawer--y-${yPosition}`]: !!yPosition }"
    data-testid="side-drawer-content"
    :style="{ width }"
    to="body"
  >
    <div class="side-drawer__header">
      <slot name="header">
        <div
          as="h2"
          class="title-l-medium"
        >
          {{ title }}
        </div>
      </slot>
      <div
        v-if="showCloseButton"
        class="side-drawer__header-close"
      >
        <BaseButton
          emphasis="low"
          icon="fa-solid fa-xmark"
          theme="mono"
          @click="closeSideDrawer()"
        />
      </div>
    </div>
    <div
      ref="side-drawer-content"
      class="side-drawer__content"
    >
      <slot name="content" />
    </div>
    <div
      v-if="$slots.buttons"
      class="side-drawer__buttons"
    >
      <slot name="buttons" />
    </div>
  </div>
</template>

<script>
import BaseButton from './BaseButton.vue'

import { library } from '@fortawesome/fontawesome-svg-core'
import { faXmark } from '@fortawesome/pro-solid-svg-icons'
import { faXmark as farXmark } from '@fortawesome/pro-regular-svg-icons'

library.add(faXmark, farXmark)

export default {
  name: 'SideDrawerContent',
  components: {
    BaseButton,
  },
  props: {
    showCloseButton: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: '',
    },
    width: {
      type: String,
      required: true,
    },
    floating: {
      type: Boolean,
      default: false,
    },
    xPosition: {
      type: String,
      default: 'right',
      validator: (value) => ['right', 'left'].includes(value),
    },
    yPosition: {
      type: String,
      default: null,
      validator: (value) => ['top', 'bottom'].includes(value),
    },
    allowBodyScroll: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close'],
  watch: {
    allowBodyScroll(newValue) {
      if (newValue) {
        document.body.classList.remove('body--side-drawer-open')
        return
      }

      document.body.classList.add('body--side-drawer-open')
    },
  },
  mounted() {
    if (this.allowBodyScroll)
      return

    document.body.classList.add('body--side-drawer-open')
  },
  beforeUnmount() {
    if (this.allowBodyScroll)
      return

    document.body.classList.remove('body--side-drawer-open')
  },
  methods: {
    closeSideDrawer() {
      this.$emit('close')
    },
    clickAway() {
      this.closeSideDrawer('overlay')
    },
  },
}
</script>

<style lang="scss">
.body--side-drawer-open {
  overflow: hidden;
}
</style>

<style lang="scss" scoped>
.drawer {
  position: fixed;
  top: 0;
  bottom: 0;
  width: 100%;
  background: white;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  padding: 16px;
  gap: 8px;
  border-radius: 12px 12px 0px 0px;
  transition: width 0.3s ease-in-out;
}

.side-drawer--no-title-gap {
  .side-drawer__content {
    margin-top: calc(0px - var(--space-3));
  }
}

.close-btn {
  position: absolute;
  top: 10px;
  left: 10px;
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
}

.side-drawer__header {
  width: 100%;
  margin: 8px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;

  p {
    margin-bottom: 0px;
    padding-bottom: var(--space-2);
  }
}

.side-drawer__content {
  overflow-y: auto;
  max-height: 100%;
  width: 100%;
  flex: 1;
}

.side-drawer__header-close {
  --font-size-2: 16px;
}

.side-drawer__buttons {
  width: 100%;
}

@media (min-width: 769px) {
  .drawer {
    border-radius: 0px;

    &.drawer--floating {
      border-radius: 12px;
      margin: var(--space-5);
    }

    &--x {
      &-right {
        right: 0;
      }

      &-left {
        left: 0;
      }
    }

    &--y {
      &-top {
        bottom: auto;
      }

      &-bottom {
        top: auto;
      }
    }
  }
}

@media (max-width: 768px) {
  .drawer {
    margin-top: var(--space-4);
    height: calc(100% - var(--space-4));
    width: 100% !important;
  }
}
</style>
